import { useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { catalogApi } from "@/lib/api/catalog";
import { downloadCsv } from "@/lib/catalog/csv-utils";

const EXPORT_HEADERS = [
  "isbn",
  "copies",
  "itemTypeCode",
  "accessionNumber",
  "bindingType",
  "lcClassification",
  "callNumber",
  "locationCode",
  "collectionCode",
  "conditionCode",
  "statusCode",
  "subjects",
  "acquisitionMode",
  "publisher",
  "placeOfPublication",
  "publishedDate",
  "title",
  "barcode",
] as const;

export function BulkExportPage() {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows: string[][] = [Array.from(EXPORT_HEADERS)];
      let page = 1;
      const pageSize = 500;
      let total = Infinity;

      while ((page - 1) * pageSize < total) {
        const response = await catalogApi.getAllResources(page, pageSize);
        total = response.total ?? response.data.length;

        for (const record of response.data) {
          if (record.type !== "physical") continue;
          const items = record.items ?? [];
          const subjects = (record.subjects ?? []).join(";");

          if (items.length === 0) {
            rows.push([
              record.isbn ?? "",
              "0",
              "",
              "",
              record.bindingType ?? "",
              record.lcClassification ?? "",
              record.callNumber ?? "",
              "",
              "",
              "",
              "",
              subjects,
              "",
              record.publisher ?? "",
              record.placeOfPublication ?? "",
              record.publishedDate ?? "",
              record.title ?? "",
              "",
            ]);
            continue;
          }

          for (const item of items) {
            rows.push([
              record.isbn ?? "",
              "1",
              item.itemType?.code ?? "",
              String(item.accessionNumber ?? ""),
              record.bindingType ?? "",
              record.lcClassification ?? "",
              item.callNumber ?? record.callNumber ?? "",
              item.location?.code ?? "",
              item.collection?.code ?? "",
              item.conditionCode ?? "",
              item.statusCode ?? "",
              subjects,
              item.acquisitionMode ?? "",
              record.publisher ?? "",
              record.placeOfPublication ?? "",
              record.publishedDate ?? "",
              record.title ?? "",
              item.barcode ?? "",
            ]);
          }
        }

        if (response.data.length < pageSize) break;
        page += 1;
      }

      if (rows.length <= 1) {
        toast.error("No physical catalog records to export.");
        return;
      }

      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(`catalog-export-${date}.csv`, rows);
      toast.success(`Exported ${rows.length - 1} row(s) to CSV.`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Failed to export catalog data.";
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bulk Export"
        description="Download physical catalog records as CSV (one row per copy)."
        showSearch={false}
      />

      <Card className="p-6 space-y-4 max-w-7xl">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-10 h-10 text-emerald-600 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm text-gray-700">
              Exports ISBN bibliographic data and copy details in a format compatible with bulk
              import.
            </p>
            <p className="text-xs text-gray-500">
              Digital-only resources are omitted. Large catalogs may take a moment.
            </p>
          </div>
        </div>
        <Button onClick={handleExport} disabled={exporting} className="gap-2">
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Download CSV
            </>
          )}
        </Button>
      </Card>
    </div>
  );
}
